// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { REORDER_PRODUCTS_ID } from "@keller/App/Widgets/ReorderPad/ReorderPad";
import ProductAddToCartButton from "@keller/Overrides/Components/ProductAddToCartButton";
import ProductDescription from "@keller/Overrides/Components/ProductDescription";
import ProductQuantityOrdered, {
    ProductQuantityOrderedActionsStyles,
} from "@keller/Overrides/Components/ProductQuantityOrdered";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@keller/Overrides/Widgets/ProductList/ProductListProductImage";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { color, ellipsis, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    extendedStyles?: ReorderPadSummaryCardStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, REORDER_PRODUCTS_ID, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
    session: getSession(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadSummaryCardStyles {
    dataCell?: DataTableCellProps;
    descriptionCell?: DataTableCellProps;
    descriptionLink?: LinkPresentationProps;
    productPrice?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productQuantityOrderedStyles?: ProductQuantityOrderedActionsStyles;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    actionsWrapper?: InjectableCss;
    addToCartSection?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productCompare?: ProductListProductImageStyles;
    addToListAndCompareWrapper?: InjectableCss;
}

export const reorderPadSummaryCardStyles: ReorderPadSummaryCardStyles = {
    descriptionCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 24px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
    descriptionLink: {
        typographyProps: {
            css: css`
                ${ellipsis(2)}
            `,
        },
    },
    productPrice: {
        price: {
            priceText: {
                size: 15,
                weight: "normal",
                css: css`
                    line-height: 24px;
                `,
            },
        },
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 15px 10px;
            padding: 15px 2px;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            css: css`
                align-self: baseline;
            `,
        },
    },
    productCompare: {
        compareCheckbox: {
            css: css`
                align-self: flex-start;
                margin-left: initial;
            `,
        },
    },
    productQuantityOrderedStyles: {
        wrapper: {
            css: css`
                min-width: 96px;
                flex-shrink: 1;
                flex-grow: 0;
            `,
        },
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
            font-size: 10px;
        `,
    },
    addToListAndCompareWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 10px;
            width: 100%;
        `,
    },
};

const ReorderPadSummaryCard: FC<Props> = ({
    product,
    productInfo,
    settingsCollection,
    session,
    updateProductInfo,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(reorderPadSummaryCardStyles, extendedStyles));

    if (!product || !productInfo) {
        return null;
    }

    const data = {
        id: product.id,
        productDescription: product.productTitle,
        lastOrderedQty: product.properties.lastOrderedQty,
        lastOrdered: product.properties.lastOrderedDate
            ? getLocalizedDateTime({
                  dateTime: new Date(product.properties.lastOrderedDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        pricing: `$0.00`,
        inventory: `$0.00`,
    };

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <DataTableCell {...styles.descriptionCell}>
                <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>{data.lastOrdered || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{data.lastOrderedQty || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {productContext?.productInfo?.pricing ? (
                    <ProductPrice
                        showLabel={false}
                        showSavings={false}
                        product={productContext}
                        extendedStyles={styles.productPrice}
                    />
                ) : (
                    EMPTY_VALUE
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                {settingsCollection.productSettings.showInventoryAvailability ? (
                    <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                ) : (
                    EMPTY_VALUE
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <StyledWrapper {...styles.actionsWrapper}>
                    {productContext.product.canAddToCart && (
                        <StyledWrapper {...styles.addToCartSection}>
                            <ProductQuantityOrdered
                                labelOverride=""
                                showActions={true}
                                extendedActionStyles={styles.productQuantityOrderedStyles}
                                id={`${productContext.product.id}-qty`}
                            />
                            <ProductAddToCartButton
                                data-test-selector={`${productContext.product.id}-addToCartBtn`}
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.addToListAndCompareWrapper}>
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                        />
                        <ProductListProductImage
                            showImage={false}
                            showBrands={false}
                            showCompare={true}
                            extendedStyles={styles.productCompare}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </DataTableCell>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadSummaryCard);
